import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Globe, CreditCard, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Modal from './ui/Modal';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'tr', name: 'Türkçe' }
];

const AccountSettingsModal = ({ isOpen, onClose }) => {
  const { user, token, isPremium, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState(user?.language || 'en');
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);

  useEffect(() => {
    if (user?.language) {
      setSelectedLanguage(user.language);
    }
  }, [user?.language]);

  const handleLanguageChange = async (langCode) => {
    setSelectedLanguage(langCode);
    setSavingLanguage(true);
    
    try {
      await axios.put(`${API_BASE}/api/auth/language`, 
        { language: langCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
    } catch (error) {
      console.error('Failed to update language:', error);
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleManagePlan = async () => {
    if (isPremium) {
      // Redirect to Stripe Customer Portal
      setLoadingPortal(true);
      try {
        const response = await axios.post(
          `${API_BASE}/api/billing/portal`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.url) {
          window.location.href = response.data.url;
        }
      } catch (error) {
        console.error('Failed to open customer portal:', error);
        // Fallback to billing page
        navigate('/app/billing');
        onClose();
      } finally {
        setLoadingPortal(false);
      }
    } else {
      // Free user - go to billing page
      navigate('/billing');
      onClose();
    }
  };

  const getSubscriptionStatus = () => {
    if (!user) return 'Free';
    
    const status = user.subscription_status;
    if (status === 'active') return 'Active';
    if (status === 'trialing') return 'Trial Active';
    if (status === 'past_due') return 'Past Due';
    if (status === 'canceled') return 'Canceled';
    return 'Free';
  };

  const getPlanName = () => {
    if (!user || user.plan === 'free') return 'Free Plan';
    return 'Pro Plan';
  };

  const modalIcon = (
    <span className="text-white font-semibold">
      {user?.email?.[0]?.toUpperCase() || 'U'}
    </span>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Account Settings"
      icon={modalIcon}
      maxWidth="max-w-md"
      testId="account-settings-modal"
    >
      {/* Content */}
      <div className="p-6 space-y-5">
        {/* Email (read-only) */}
        <div>
          <label className="text-sm font-medium" style={{ color: '#A1A1AA' }}>
            Email
          </label>
          <div 
            className="mt-1.5 px-4 py-3 rounded-lg flex items-center gap-3"
            style={{ 
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            <User className="w-4 h-4" style={{ color: '#71717A' }} />
            <span style={{ color: '#FAFAFA' }}>{user?.email || 'Not available'}</span>
          </div>
        </div>

        {/* Language */}
        <div>
          <label className="text-sm font-medium flex items-center gap-2" style={{ color: '#A1A1AA' }}>
            <Globe className="w-4 h-4" />
            Language
          </label>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                disabled={savingLanguage}
                className="px-4 py-2.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: selectedLanguage === lang.code 
                    ? 'linear-gradient(135deg, rgba(124, 92, 255, 0.3) 0%, rgba(91, 140, 255, 0.2) 100%)'
                    : 'rgba(255,255,255,0.03)',
                  border: selectedLanguage === lang.code 
                    ? '1px solid rgba(124, 92, 255, 0.5)'
                    : '1px solid rgba(255,255,255,0.1)',
                  color: selectedLanguage === lang.code ? '#FAFAFA' : '#A1A1AA'
                }}
                data-testid={`lang-${lang.code}`}
              >
                {lang.name}
                {savingLanguage && selectedLanguage === lang.code && (
                  <Loader2 className="w-3 h-3 ml-2 inline animate-spin" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Current Plan */}
        <div>
          <label className="text-sm font-medium flex items-center gap-2" style={{ color: '#A1A1AA' }}>
            <CreditCard className="w-4 h-4" />
            Subscription
          </label>
          <div 
            className="mt-1.5 px-4 py-3 rounded-lg flex items-center justify-between"
            style={{ 
              background: isPremium 
                ? 'linear-gradient(135deg, rgba(124, 92, 255, 0.15) 0%, rgba(91, 140, 255, 0.1) 100%)'
                : 'rgba(255,255,255,0.03)',
              border: isPremium 
                ? '1px solid rgba(124, 92, 255, 0.3)'
                : '1px solid rgba(255,255,255,0.1)'
            }}
          >
            <div>
              <p className="font-medium" style={{ color: '#FAFAFA' }}>
                {getPlanName()}
              </p>
              <p className="text-xs mt-0.5" style={{ color: isPremium ? '#7C5CFF' : '#71717A' }}>
                {getSubscriptionStatus()}
              </p>
            </div>
            {isPremium && (
              <span 
                className="px-2 py-1 rounded text-xs font-medium"
                style={{ 
                  background: 'rgba(124, 92, 255, 0.2)',
                  color: '#A78BFA'
                }}
              >
                PRO
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div 
        className="px-6 py-4 border-t flex gap-3"
        style={{ borderColor: 'rgba(255,255,255,0.1)' }}
      >
        <button
          onClick={handleManagePlan}
          disabled={loadingPortal}
          className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
          style={{
            background: 'linear-gradient(135deg, #7C5CFF 0%, #6B4FE0 100%)',
            color: 'white'
          }}
          data-testid="manage-plan-btn"
        >
          {loadingPortal ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isPremium ? (
            'Manage Plan'
          ) : (
            'Upgrade to Pro'
          )}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{
            background: 'rgba(255,255,255,0.05)',
            color: '#A1A1AA',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default AccountSettingsModal;
